import { FC, useEffect, useRef, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SVGIcon from 'react-inlinesvg';

import IconThankYou from '@assets/svg/thank-you.svg';
import { Button, Loader } from '@components';
import { postSource } from '@store';
import { EMAIL_REGEXP } from '@utilities';

import styles from '../index.module.scss';

export type Fields = {
	name: string;
	note?: string;
	from_email: string;
};

type Props = {
	onClose: () => void;
};

export const SourceForm: FC<Readonly<Props>> = ({ onClose }) => {
	const { t } = useTranslation();
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const timer = useRef<NodeJS.Timeout | null>(null);
	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<Fields>();

	const onFormSubmit: SubmitHandler<Fields> = async (data: Fields) => {
		setLoading(true);

		try {
			await postSource({
				...data,
				note: 'Sample note'
			});

			setError(false);
			setSuccess(true);

			timer.current = setTimeout(() => {
				onClose();
				setSuccess(false);
			}, 2000);
		} catch (e) {
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		return () => {
			if (typeof timer.current === 'number') {
				clearTimeout(timer.current);
			}
		};
	}, []);

	return (
		<form onSubmit={handleSubmit(onFormSubmit)} className={styles.form} data-testid="source-form">
			{!success && <h2>{t('Add Your Source to dmarc.io')}</h2>}

			{loading && (
				<div className={styles.loader}>
					<Loader />
				</div>
			)}

			{error && <p className={styles.formError}>{t('Something went wrong.')}</p>}

			{success ? (
				<p className={styles.success}>
					<SVGIcon src={IconThankYou} />

					{t('for submitting your source!')}
				</p>
			) : (
				<>
					<div className={styles.row}>
						<label className={styles.label} htmlFor="email">
							{t('Email')}
						</label>

						<input
							{...register('from_email', { required: true, pattern: EMAIL_REGEXP })}
							id="email"
							className={styles.field}
						/>

						{errors.from_email && <p className={styles.error}>{t('Please use a valid email address.')}</p>}
					</div>

					<div className={styles.row}>
						<label className={styles.label} htmlFor="source">
							{t('Source')}
						</label>

						<input {...register('name', { required: true })} id="source" className={styles.field} />

						{errors.name && <p className={styles.error}>{t('The "source" field is required.')}</p>}
					</div>

					<div className={styles.actions}>
						<Button type="button" outline={true} onClick={onClose}>
							{t('Cancel')}
						</Button>

						<Button type="submit">{t('Send')}</Button>
					</div>
				</>
			)}
		</form>
	);
};

export default SourceForm;
